import {
  makeStyles,
  tokens,
  Text,
  Body2,
  Caption1,
  Badge,
} from '@fluentui/react-components';
import type { RuntimeBlindSpot } from '../types';
import { formatBlindSpotType, getSeverityBadgeColor, parseSignals } from '../utils/scoring';
import { format } from 'date-fns';
import {
  DetailDrawer,
  DetailSection,
  DetailBadges,
} from '../../../components/common/DetailDrawer';

interface BlindSpotDetailDrawerProps {
  blindSpot: RuntimeBlindSpot | null;
  open: boolean;
  onClose: () => void;
}

const useStyles = makeStyles({
  signalItem: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: `${tokens.spacingVerticalXS} 0`,
  },
  meta: {
    display: 'flex',
    gap: tokens.spacingHorizontalM,
    color: tokens.colorNeutralForeground3,
    flexWrap: 'wrap',
  },
});

export function BlindSpotDetailDrawer({ blindSpot, open, onClose }: BlindSpotDetailDrawerProps) {
  const styles = useStyles();

  const signals = blindSpot ? parseSignals(blindSpot.signals) : [];

  return (
    <DetailDrawer
      open={open}
      onClose={onClose}
      title={blindSpot ? formatBlindSpotType(blindSpot.blindSpotType) : ''}
    >
      {blindSpot && (
        <>
          <DetailBadges>
            <Badge appearance="filled" color={getSeverityBadgeColor(blindSpot.severity)}>
              {blindSpot.severity}
            </Badge>
            <Badge appearance="outline">
              {blindSpot.status}
            </Badge>
            <Badge appearance="tint" color="informative">
              {blindSpot.confidence}% confidence
            </Badge>
          </DetailBadges>

          <div className={styles.meta}>
            <Caption1>{blindSpot.scopeType}: {blindSpot.scopeId}</Caption1>
            <Caption1>First seen: {format(new Date(blindSpot.firstSeenAt), 'MMM d, yyyy')}</Caption1>
            <Caption1>Last seen: {format(new Date(blindSpot.lastSeenAt), 'MMM d, yyyy')}</Caption1>
          </div>

          {/* Recommended Action */}
          <DetailSection title="Recommended Action" divider>
            <Body2>{blindSpot.recommendedAction}</Body2>
          </DetailSection>

          {/* Signals Evidence */}
          {signals.length > 0 && (
            <DetailSection title="Signals Evidence">
              {signals.map((signal) => (
                <div key={signal.key} className={styles.signalItem}>
                  <Caption1>{signal.key}</Caption1>
                  <Text size={300} weight="semibold">{signal.value}</Text>
                </div>
              ))}
            </DetailSection>
          )}

          {/* Raw signals JSON fallback */}
          {signals.length === 0 && blindSpot.signals && (
            <DetailSection title="Signals (Raw)">
              <pre style={{
                background: tokens.colorNeutralBackground3,
                padding: tokens.spacingVerticalS,
                borderRadius: tokens.borderRadiusMedium,
                fontSize: '12px',
                overflow: 'auto',
                maxHeight: '200px',
                whiteSpace: 'pre-wrap',
                wordBreak: 'break-word',
              }}>
                {blindSpot.signals}
              </pre>
            </DetailSection>
          )}

          {/* Metadata */}
          <DetailSection title="Details" divider>
            <div className={styles.signalItem}>
              <Caption1>Reference ID</Caption1>
              <Text size={200}>{blindSpot.referenceId}</Text>
            </div>
            <div className={styles.signalItem}>
              <Caption1>Internal ID</Caption1>
              <Text size={200}>{blindSpot.id}</Text>
            </div>
            <div className={styles.signalItem}>
              <Caption1>Blind Spot Type</Caption1>
              <Text size={200}>{blindSpot.blindSpotType}</Text>
            </div>
          </DetailSection>
        </>
      )}
    </DetailDrawer>
  );
}
